import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CONTACT_EMAIL, CONTACT_WHATSAPP_DISPLAY, CONTACT_WHATSAPP_URL } from '../../config/contact';
import { trackEvent } from '../../utils/tracking';
import { buildWhatsAppUrl, getClickId } from '../../utils/click-id';

type FormStatus = 'idle' | 'sending' | 'error';

const SEND_ERROR_MESSAGE = `No hemos podido enviar tu mensaje. Escríbenos directamente a ${CONTACT_EMAIL} o por WhatsApp al ${CONTACT_WHATSAPP_DISPLAY}.`;

export function ContactFormSection() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, click_id: getClickId() }),
      });
      const data = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        setErrorMsg(response.status === 400 && data.message ? data.message : SEND_ERROR_MESSAGE);
        setStatus('error');
        return;
      }
      navigate('/gracias');
    } catch {
      setErrorMsg(SEND_ERROR_MESSAGE);
      setStatus('error');
    }
  }

  return (
    <section id="contacto" className="section section--muted contact-form-section">
      <div className="container">
        <div style={{ maxWidth: '38rem', marginInline: 'auto' }}>
          <div className="section__header text-center" style={{ marginInline: 'auto' }}>
            <h2>¿Hablamos de tu local?</h2>
            <p className="muted">
              Cuéntanos qué tipo de local tienes, qué equipos utilizas y en qué necesitas ayuda.
              Si hace falta instalación o una intervención técnica, te damos presupuesto antes de empezar.
            </p>
          </div>
          <div className="ui-card" style={{ padding: '2rem' }}>
            <form className="stack" onSubmit={handleSubmit}>
              <div className="grid-2">
                <Input label="Nombre *" id="contact-name" name="name" type="text" autoComplete="name"
                  required placeholder="Tu nombre" value={form.name} onChange={handleChange} />
                <Input label="Teléfono" id="contact-phone" name="phone" type="tel" autoComplete="tel"
                  placeholder="Opcional" value={form.phone} onChange={handleChange} />
              </div>
              <Input label="Correo electrónico *" id="contact-email" name="email" type="email"
                autoComplete="email" required placeholder="tu@email.com" value={form.email} onChange={handleChange} />
              <label className="ui-field" htmlFor="contact-message">
                <span className="ui-field__label">Mensaje *</span>
                <textarea id="contact-message" name="message" className="ui-textarea" required rows={5}
                  placeholder="Cuéntanos en qué podemos ayudarte..." value={form.message} onChange={handleChange} />
              </label>
              <p id="contact-error" role="alert" aria-live="polite" style={{
                margin: 0, minHeight: '2.6rem', fontSize: '0.88rem', color: 'var(--color-danger)',
                visibility: status === 'error' ? 'visible' : 'hidden',
              }}>
                {errorMsg}
              </p>
              <Button type="submit" disabled={status === 'sending'} style={{ width: '100%' }}>
                {status === 'sending' ? 'Enviando…' : 'Enviar mensaje'}
              </Button>
            </form>
            <div className="muted text-center" style={{
              marginTop: '1.5rem', paddingTop: '1.25rem',
              borderTop: '1px solid var(--color-border)', fontSize: '0.9rem',
            }}>
              <p style={{ margin: '0 0 0.5rem' }}>También puedes escribirnos directamente:</p>
              <p style={{ margin: 0 }}>
                <a href={CONTACT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', fontWeight: 600 }}
                  onClick={(event) => {
                    event.currentTarget.href = buildWhatsAppUrl(CONTACT_WHATSAPP_URL);
                    trackEvent('whatsapp_click');
                  }}>
                  WhatsApp {CONTACT_WHATSAPP_DISPLAY}
                </a>
                {' · '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
